import {
  useState,
  useEffect,
} from 'react';

import {
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
  type Coupon,
} from '@gymcloud/core';

import {
  Popup,
  Button,
  Spinner,
  NotFound,
  NamespaceLogo,
  MobileCheckout,
} from 'web-components';

import {
  Flex,
  Text,
  H200,
  icons,
} from 'web-styled';

import Overview from './Overview';
import Item from './Item';

import * as S from './styled';

const INSTRUCTIONS = 'https://support.gymcloud.com/support/solutions/articles/3000101277-how-to-update-your-subscription-plan-or-payment-method#mobsub'
const IOS_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions'
const ANDROID_SUBSCRIPTIONS_URL = 'https://play.google.com/store/account/subscriptions'

type FormStep = 'upgrade' | 'plans' | 'checkout' | 'finish' | 'overview' | 'cancel' | 'successfully_canceled' | 'mobile_signup'

const mapState = (state: any) => ({
  profile: coreSelectors.getProfile(state),
  readStatus: getStatus(
    state,
    [
      'productPlans.requests.fetchProductPlans.status',
    ],
    true,
  ),
  updateStatus: getStatus(
    state,
    [
      'paymentIntents.requests.buyProduct.status',
      'paymentIntents.requests.finishBuy.status',
      'paymentMethods.requests.create.status',
      'paymentMethods.requests.delete.status',
      'paymentMethods.requests.setDefault.status',
    ],
  ),
  productPlans: coreSelectors.getResourceMappedList(
    'productPlans',
  )(state),
  subscription: coreSelectors.getSubscription(state),
});

const SuccessMessage = ({ email }: { email: string }) => (
  <Flex
    column
    spacing={20}
    padding="0 15px"
    width="100%"
    align="center"
    justify="center"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="122"
      height="122"
      fill="none"
      viewBox="0 0 122 122"
    >
      <path
        stroke="#787655"
        strokeWidth="8.304"
        d="M61 117.848c31.396 0 56.848-25.452 56.848-56.848S92.396 4.152 61 4.152 4.152 29.604 4.152 61 29.604 117.848 61 117.848Z"
      />
      <path
        stroke="#787655"
        strokeWidth="8.304"
        d="m36.6 63.076 16.225 16.226 31.84-31.832"
      />
    </svg>
    <Flex column spacing={10} width="100%" align="center">
      <H200 color="#121827">Thank You!</H200>
      <Text>
        Order details will be sent to<br />
        <b>{email}</b>.
      </Text>
    </Flex>
  </Flex>
)

const SubscriptionPage = () => {
  const [menuisOpen, setMenuisOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [formStep, setFormStep] = useState<FormStep>('upgrade')
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [couponState, setCouponState] = useState<any>({ value: '' });
  const [selectedPlan, setSelectedPlan] = useState<any | null>();
  const {
    profile,
    readStatus,
    updateStatus,
    subscription,
    productPlans,
  } = useSelector(mapState);
  const dispatch = useDispatch();

  useEffect(() => {
    const status = profile.subscription_status
    if (status === 'Trialing' || status === 'Active' || status === 'Canceled') {
      setFormStep(profile.is_paid_via_stripe ? 'overview' : 'mobile_signup')
    }
  }, [profile.subscription_status])

  const profileActions = [
    {
      icon: 'signout',
      name: 'Sign out',
      action: () => dispatch(
        coreActions.logoutRequest(),
      ),
    },
  ]


  const handleCheckCoupon = (coupon: string) => {
    setCouponState(s => ({
      ...s,
      loading: true,
    }))
    dispatch(
      coreActions.requestCheckCoupon({
        coupon,
        callbacks: {
          onSuccess: (res: Coupon) => {
            setCoupon(res)
            setCouponState(s => ({
              ...s,
              loading: false,
              status: 'valid',
            }))
          },
          onError: (res: Coupon) => {
            setCoupon(res)
            setCouponState(s => ({
              ...s,
              loading: false,
              status: 'invalid',
            }))
          },
        },
      }),
    );
  }

  const onSubscribe = () => {
    setLoading(true);
    const setPlanSuccess = () => {
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalWorkouts',
        }),
      );
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalPrograms',
        }),
      );
      setLoading(false);
      setFormStep('finish')
    };

    const action = subscription.id
      ? coreActions.requestUpdateSubscription
      : coreActions.requestSetPlan;

    const body: Record<string, any> = {
      plan_id: selectedPlan.id,
    }

    if (coupon) {
      body.coupon = coupon.id;
    }

    dispatch(
      action({
        id: subscription.id,
        body,
        isClient: true,
        callbacks: {
          onError: () => {
            setLoading(false);
          },
          onSuccess: setPlanSuccess,
        },
      }),
    );
  }

  const handleCancelSubscription = () => {
    dispatch(
      coreActions.requestCancelSubscription({
        id: subscription?.id,
        callbacks: {
          onSuccess: () => {
            setFormStep('successfully_canceled')
          }
        }
      }),
    )
  }

  return (
    <S.PageWrap>
      <S.Header>
        My Account
        <S.Dropdown>
          <Popup
            isOpen={menuisOpen}
            trigger={(
              <Flex>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="38"
                  height="38"
                  fill="none"
                  viewBox="0 0 38 38"
                >
                  <path
                    stroke="#fff"
                    strokeLinecap="round"
                    strokeMiterlimit="10"
                    strokeWidth="3.563"
                    d="M6.531 11.281H31.47M6.531 19H31.47M6.531 26.719H31.47"
                  />
                </svg>
              </Flex>
            )}
            setPopupVisible={() => setMenuisOpen(!menuisOpen)}
            onClickOutside={() => setMenuisOpen(false)}
          >
            <S.Actions>
              {profileActions.map(item => (
                <S.Action
                  key={item.name}
                  onClick={() => {
                    item.action();
                    setMenuisOpen(false);
                  }}
                >
                  <Flex flex="0 0 25%">
                    <S.ActionIcon
                      icon={item.icon}
                      style={{
                        backgroundImage: `url(${icons[item.icon]})`,
                      }}
                    />
                  </Flex>
                  <span>{item.name}</span>
                </S.Action>
              ))}
            </S.Actions>
          </Popup>
        </S.Dropdown>
      </S.Header>

      {readStatus.succeeded && ({
        upgrade: (
          <S.FormWrap>
            <NamespaceLogo />
            <Flex column spacing={20}>
              <Text size="16px" color="#231F20" align="center">
                If you are committed to building the strongest version of yourself, <br />
                let StrongFirst show you the way.
              </Text>
              <Flex width="100%" justify="center">
                <Text size="16px" weight="600" color="#231F20" align="center">
                  Upgrade now to unlock:
                </Text>
              </Flex>
              <ul>
                <li>Premium practices, programs, and exercise library </li>
                <li>100+ total weeks of programming </li>
                <li>Content updates every 6-8 weeks</li>
                <li>Exclusive features like the Practice of the Day</li>
              </ul>
            </Flex>
            <S.SubmitButton onClick={() => setFormStep('plans')}>
              Shop Plans
            </S.SubmitButton>
          </S.FormWrap>
        ),
        plans: (
          <S.FormWrap>
            <Flex column spacing={20} width="100%">
              <Text size="1.6em" weight="600" color="#333333">Choose Your Plan</Text>

              {productPlans.map((plan) => (
                <Item
                  key={plan.id}
                  name={plan.name}
                  amount={plan.amount}
                  interval={plan.interval}
                  isDisabled={plan.id === Number(subscription?.plan?.id)}
                  isSelected={plan.id === selectedPlan?.id}
                  onSelect={() => {
                    setSelectedPlan(plan);
                  }}
                />
              ))}
            </Flex>

            <Flex column align="center" width="100%">
              <S.SubmitButton
                isDisabled={!selectedPlan?.id}
                onClick={() => setFormStep('checkout')}
              >
                Select Plan
              </S.SubmitButton>

              {subscription?.id && (
                <Button
                  spacing="big"
                  alignSelf="center"
                  appearance="link"
                  onClick={() => setFormStep('overview')}
                >
                  Back
                </Button>
              )}
            </Flex>
          </S.FormWrap>
        ),
        checkout: (
          <S.FormWrap>
            {selectedPlan?.id && (
              <MobileCheckout
                coupon={coupon}
                couponState={couponState}
                plan={{
                  id: selectedPlan.id,
                  price: String(selectedPlan.amount),
                  name: selectedPlan.name,
                  interval: selectedPlan.interval,
                }}
                onCheckCoupon={handleCheckCoupon}
              />
            )}

            <Flex column align="center" width="100%">
              <S.SubmitButton
                isLoading={isLoading}
                isDisabled={!selectedPlan?.id}
                onClick={onSubscribe}
              >
                Subscribe
              </S.SubmitButton>
              <Button
                spacing="big"
                alignSelf="center"
                appearance="link"
                onClick={() => setFormStep('plans')}
              >
                Back
              </Button>
            </Flex>
          </S.FormWrap>
        ),
        finish: (
          <S.FormWrap>
            <SuccessMessage email={profile.email} />

            <S.SubmitButton onClick={() => setFormStep('overview')}>
              Close
            </S.SubmitButton>
          </S.FormWrap>
        ),
        overview: (
          <Flex
            column
            spacing={50}
            align="center"
            width="100%"
            padding="30px 25px"
          >
            <Overview
              couponState={couponState}
              goTo={(step: string) => setFormStep(step as FormStep)}
              onCheckCoupon={handleCheckCoupon}
            />
          </Flex>
        ),
        cancel: (
          <S.FormWrap>
            <Flex column spacing={15} align="center">
              <Text size="18px" weight="600">Cancel Subscription</Text>
              <Text align="center">Are you sure you want to cancel your premium subscription?</Text>
            </Flex>

            <Flex column align="center" width="100%">
              <S.SubmitButton onClick={handleCancelSubscription}>
                Confirm
              </S.SubmitButton>

              <Button
                spacing="big"
                alignSelf="center"
                appearance="link"
                onClick={() => setFormStep('overview')}
              >
                No, I changed my mind!
              </Button>
            </Flex>
          </S.FormWrap>
        ),
        successfully_canceled: (
          <S.FormWrap>
            <Flex column spacing={15} align="center">
              <Text size="18px" weight="600">Subscription Cancelled</Text>
              <Text align="center">
                You will be able to access your premium subscription content
                until the scheduled date of your next payment. At that time,
                your account will be downgraded to Freemium status and
                you will be able to access only your free content.
              </Text>
            </Flex>

            <Flex column width="100%">
              <S.SubmitButton onClick={() => setFormStep('overview')}>
                Ok
              </S.SubmitButton>
            </Flex>
          </S.FormWrap>
        ),
        mobile_signup: (
          <S.CardWrap>
            <NamespaceLogo />
            <Flex column spacing={20}>
              <Text size="16px" color="#333333" align="center">
                Because you signed up through the App Store or Google Play,
                your subscription cannot be managed on the web.
              </Text>
            </Flex>

            <S.Card>
              <Flex column width="100%" height="130px" align="center" justify="center">
                <Text size="16px" color="#333333" weight="700">To Cancel</Text>
                <Text size="16px">Open your phone subscriptions</Text>
                <a target="_blank" href={IOS_SUBSCRIPTIONS_URL}>iOS (App Store)</a>
                <a target="_blank" href={ANDROID_SUBSCRIPTIONS_URL}>Android (Google Play)</a>
              </Flex>
              <hr />
              <Flex column width="100%" height="130px" align="center" justify="center">
                <Text size="16px" color="#333333" weight="700">To Change Subscriptions</Text>
                <Flex spacing={3}>
                  <Text size="16px" color="#333333">Follow the</Text>
                  <a target="_blank" href={INSTRUCTIONS}>instructions here</a>
                </Flex>
              </Flex>
            </S.Card>
          </S.CardWrap>
        ),
      }[formStep])}

      {readStatus.pending && (
        <Spinner message="Loading Plans..." />
      )}

      {readStatus.failed && (
        <NotFound title="Something went wrong" />
      )}

      {updateStatus.pending && (
        <Spinner over />
      )}
    </S.PageWrap>
  )
};

export default SubscriptionPage;
